import axios from 'axios';
import * as Analytics from '@/Util/reporting.js';

export default async (commit, getters) => {
    const
        randomMin = 1000000000000000,
        randomMax = 9007199254740991,
        randomNumber = Math.floor(Math.random() * (randomMax - randomMin + 1)) + randomMin;

    await commit('setTransactionId', new String(randomNumber));

    Analytics.pushToDataLayer({
        event: 'purchase_submitted',
        attributes: {
            section: 'Submit Purchase',
            action: 'Purchase Submitted',
        },
    });

    let { billingPeriod, coupons, selectedAddons, selectedCharges, selectedPlanId} = getters.cartInfo;

    let includedAddons = getters.includedAddons.map(({itemId, quantity}) => {
        return {
            itemId,
            quantity
        }
    });

    let includedCharges = getters.includedCharges.map(({itemId, quantity}) => {
        return {
            itemId,
            quantity
        }
    });

    // This handles addons that are mandatory but still can be modified by the user.
    let tmpIncludedAddons = includedAddons.map(item => item.itemId);
    selectedAddons = selectedAddons.filter(item => {
        if (tmpIncludedAddons.includes(item.itemId)) {
            includedAddons.forEach(i => {
                if (i.itemId === item.itemId) {
                    i.quantity = item.quantity;
                }
            })
        }

        return !tmpIncludedAddons.includes(item.itemId);
    });

    let payload = {
        plan: selectedPlanId,
        billingPeriod: 1,
        billingPeriodUnit: billingPeriod,
        installments: getters.cartInfo.installments.count,
        currency: getters.userInfo.currency,
        addons: [
            ...includedAddons,
            ...selectedAddons
        ],
        charges: [
            ...includedCharges,
            ...selectedCharges
        ],
        couponCodes: coupons,
        userInfo: {
            firstName: getters.userInfo.firstName,
            lastName: getters.userInfo.lastName,
            email: getters.userInfo.email,
            phone: getters.userInfo.phone,
            currency: getters.userInfo.currency,
            paymentToken: getters.userInfo.paymentToken,
        },
        billingAddress: {
            company: getters.userInfo.company,
            line1: getters.userInfo.line1,
            line2: getters.userInfo.line2,
            city: getters.userInfo.city,
            stateCode: getters.userInfo.state,
            postalCode: getters.userInfo.postalCode,
            countryCode: getters.userInfo.country,
        },
        ...(getters.isPartnerLink && {partnerAccountId: getters.purchaseInfo.partnerAccountId}),
        ...(getters.isPurchaseLink && {purchaseLinkId: getters.purchaseInfo.purchaseLinkId}),
        ...(!!getters.purchaseInfo.bundleId && {bundleId: getters.purchaseInfo.bundleId}),
        ...(!!getters.purchaseInfo.linkPostedBy && {linkPostedBy: getters.purchaseInfo.linkPostedBy}),
        ...(!!getters.purchaseInfo.customerId && {customerId: getters.purchaseInfo.customerId}),
        ...(!!getters.purchaseInfo.subscriptionId && {subscriptionId: getters.purchaseInfo.subscriptionId}),
        ...(!!getters.purchaseInfo.sourceCode && {adPartner: getters.purchaseInfo.sourceCode}),
        talon: getters.talon,
        etfSeen: getters.purchaseInfo.showEtf,
        tcAccepted: getters.cartInfo.acceptedTc,
        mbg90Accepted: getters.cartInfo.acceptedMbg90,
        installmentsAccepted: getters.cartInfo.acceptedInstallments,
        tracking: JSON.stringify(getters.tracking),
        ...(!!getters.purchaseInfo.quoteId && {quoteId: getters.purchaseInfo.quoteId}),
        ...(!!getters.purchaseInfo.channel && {channel: getters.purchaseInfo.channel}),
        ...(getters.isUpgrading && {subscriptionId: getters.userInfo.subscriptionId, customerId: getters.userInfo.customerId}),
        ...(getters.isAddingSub && {customerId: getters.userInfo.customerId}),
        transactionId: getters.transactionId,
        ...(!!getters.purchaseInfo.linkId && {linkId: getters.purchaseInfo.linkId}),
        ...(!!getters.hasThryvCrossSell && {thryvCrossSell: getters.thryvCrossSell}),
    };

    try {
        let action = 'create';
        if (getters.isUpgrading) {
            action = 'update';
        } else if (getters.isAddingSub) {
            action = 'add';
        }
        let { data } = await axios.post(`/api/v1/subscription/${action}`, {
            ...payload
        });

        await commit('updatePurchaseInfo', {
            totalPaid: data.totalPaid,
            invoiceTotal: data.invoiceTotal,
            cardUsed: {
                last4: data.card.last4,
                type: data.card.type,
            }
        });
        await commit('updateUserInfo', {
            subscriptionId: data.subscriptionId
        });
        await commit('setSubmitting', false);
        await commit('setSubmittedSuccess', true);
    } catch (error) {
        commit('setSubmitting', false);
        if (window.axios.isAxiosError(error)) {
            if (!!error.response) {
                const errorObj = error.response.data;
                window.Sentry.logError(error, {server_event_id: error.response.data.eventId});
                commit('setError', {
                    active: true,
                    code: errorObj.error,
                    message: errorObj.message
                });
            } else {
                window.Sentry.logError(error);
                commit('setError', {
                    active: true,
                    code: window.constants.errorCodes.generalError,
                    message: window.constants.errorMessages.generalError
                });
            }
        } else if (!getters.hasActiveError) {
            window.Sentry.logError(error);
            commit('setError', {
                active: true,
                code: window.constants.errorCodes.generalError
            });
        }
    }

    // INFO: Handle all analytic tracking. This is done separate from he rest of the processes to avoid throwing errors that dont effect the user.
    if (!!coupons && Array.isArray(coupons)) {
        coupons = coupons.join(', ');
    }

    if (getters.orderComplete) {
        try {
            Analytics.pushToDataLayer({
                event: 'purchase_completed',
                attributes: {
                    section: 'Submit Purchase',
                    action: 'Purchase Succeeded',
                    transaction_id: getters.transactionId,
                    event_properties: {
                        customer_city: getters.userInfo.city,
                        customer_country: getters.userInfo.country,
                        customer_firstname: getters.userInfo.firstName,
                        customer_lastname: getters.userInfo.lastName,
                        customer_company: getters.userInfo.company,
                        customer_state: getters.userInfo.state,
                        customer_zip: getters.userInfo.postalCode,
                        num_contacts: getters.totalContacts,
                        num_users: getters.totalUsers,
                        promo_code: coupons,
                        ...(!!getters.purchaseInfo.partnerAccountId && {partner_account_id: getters.purchaseInfo.partnerAccountId}),
                        ...(!!getters.purchaseInfo.linkPostedBy && {link_posted_by_id: getters.purchaseInfo.linkPostedBy}),
                        selected_edition: getters.selectedPlanId,
                        ...(
                            !!payload.addons.find(item => item.itemId.toLowerCase().includes('additional-contact'))
                            && {
                                selected_contact_plans: payload.addons.find(item => item.itemId.toLowerCase().includes('additional-contact')).quantity
                            }
                        ),
                        ...(
                            !!payload.addons.find(item => item.itemId.toLowerCase().includes('additional-user'))
                            && {
                                selected_user_plans: payload.addons.find(item => item.itemId.toLowerCase().includes('additional-user')).quantity
                            }
                        ),
                        total: getters.totals.total - getters.totals.invoiceDiscountTotal.total,
                        tax: getters.tax,
                    },
                },
            });
        } catch (error) {
            // Ignore error. Its just tracking.
        }
    } else if (getters.hasActiveError) {
        try {
            Analytics.pushToDataLayer({
                event: 'purchase_failed',
                attributes: {
                    section: 'Submit Purchase',
                    action: 'Purchase Failed',
                    event_properties: {
                        customer_city: getters.userInfo.city,
                        customer_country: getters.userInfo.country,
                        customer_firstname: getters.userInfo.firstName,
                        customer_lastname: getters.userInfo.lastName,
                        customer_company: getters.userInfo.company,
                        customer_state: getters.userInfo.state,
                        customer_zip: getters.userInfo.postalCode,
                        num_contacts: getters.totalContacts,
                        num_users: getters.totalUsers,
                        promo_code: coupons,
                        ...(!!getters.purchaseInfo.partnerAccountId && {partner_account_id: getters.purchaseInfo.partnerAccountId}),
                        ...(!!getters.purchaseInfo.linkPostedBy && {link_posted_by_id: getters.purchaseInfo.linkPostedBy}),
                        selected_edition: getters.selectedPlanId,
                        ...(
                            !!payload.addons.find(item => item.itemId.toLowerCase().includes('additional-contact'))
                            && {
                                selected_contact_plans: payload.addons.find(item => item.itemId.toLowerCase().includes('additional-contact')).quantity
                            }
                        ),
                        ...(
                            !!payload.addons.find(item => item.itemId.toLowerCase().includes('additional-user'))
                            && {
                                selected_user_plans: payload.addons.find(item => item.itemId.toLowerCase().includes('additional-user')).quantity
                            }
                        ),
                        total: getters.totals.total - getters.totals.invoiceDiscountTotal.total,
                        tax: getters.tax,
                    },
                },
            });
        } catch (error) {
            // Ignore error. Its just tracking.
        }
    }
}
